import {
  createContext,
  type FocusEventHandler,
  type PropsWithChildren,
  type RefObject,
  use,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';
import { noop } from '@interxfi/utilities/fn/no-op';

export const TABLE_OF_CONTENTS_SCROLL_SENTRY_DATA_ATTRIBUTE = 'data-table-of-contents-scroll-sentry';

interface TableOfContentsSentryDataAttributes {
  [TABLE_OF_CONTENTS_SCROLL_SENTRY_DATA_ATTRIBUTE]: string;
}

type TableOfContentsFocusInSectionProps<T extends string = string> = {
  id: T;
  onFocusCapture: FocusEventHandler<HTMLElement>;
};

interface ITableOfContentsContext<T extends string = string> {
  activeTab: T | undefined;
  id: string;
  setActiveTab: (tabId: T) => void;
  getTabFocusInSectionProps: (tabId: T) => TableOfContentsFocusInSectionProps<T>;
  tableOfContentsSentryDataAttributes: TableOfContentsSentryDataAttributes;
  getTableOfContentsSentries: () => NodeListOf<HTMLElement>;
  isProgrammaticScrollRef: RefObject<boolean>;
  tocRef: RefObject<HTMLElement | null>;
}

const TableOfContentsContext = createContext<ITableOfContentsContext<any>>({
  activeTab: undefined,
  id: '',
  setActiveTab: noop,
  getTabFocusInSectionProps: () => ({}) as any,
  tableOfContentsSentryDataAttributes: { [TABLE_OF_CONTENTS_SCROLL_SENTRY_DATA_ATTRIBUTE]: '' },
  getTableOfContentsSentries: () => [] as unknown as NodeListOf<HTMLElement>,
  isProgrammaticScrollRef: { current: false },
  tocRef: { current: null },
});

export function TableOfContentsContextProvider<T extends string = string>({ children }: PropsWithChildren) {
  const id = useId();
  const isProgrammaticScrollRef = useRef(false);
  const [activeTab, setActiveTab] = useState<T | undefined>(undefined);
  const tocRef = useRef<HTMLElement | null>(null);

  const getTabFocusInSectionProps = useCallback((tabId: T) => {
    return {
      id: tabId,
      onFocusCapture: () => {
        setActiveTab(tabId);
      },
    } as const satisfies TableOfContentsFocusInSectionProps<T>;
  }, []);

  const tableOfContentsSentryDataAttributes = useMemo(() => {
    return {
      [TABLE_OF_CONTENTS_SCROLL_SENTRY_DATA_ATTRIBUTE]: id,
    } as const;
  }, [id]);

  const getTableOfContentsSentries = useCallback(() => {
    return document.querySelectorAll<HTMLElement>(`[${TABLE_OF_CONTENTS_SCROLL_SENTRY_DATA_ATTRIBUTE}="${id}"]`);
  }, [id]);

  const value = useMemo(
    () =>
      ({
        activeTab,
        getTabFocusInSectionProps,
        id,
        setActiveTab,
        tableOfContentsSentryDataAttributes,
        getTableOfContentsSentries,
        isProgrammaticScrollRef,
        tocRef,
      }) as ITableOfContentsContext<T>,
    [id, activeTab, getTabFocusInSectionProps, tableOfContentsSentryDataAttributes, getTableOfContentsSentries],
  );

  return <TableOfContentsContext.Provider value={value}>{children}</TableOfContentsContext.Provider>;
}

export function useTableOfContentsContext<T extends string = string>() {
  return use<ITableOfContentsContext<T>>(TableOfContentsContext);
}

interface TableOfContentsScrollHandlerOptions {
  onActiveTabChange?: (activeTabId: string) => void;
  fromTop?: number;
}

export function useTableOfContentsScrollHandler<T extends string = string>({ onActiveTabChange, fromTop }: TableOfContentsScrollHandlerOptions = {}) {
  const { getTableOfContentsSentries, id, isProgrammaticScrollRef, setActiveTab, tocRef } = useTableOfContentsContext<T>();

  useEffect(() => {
    const handleScroll = () => {
      if (isProgrammaticScrollRef.current) {
        return;
      }

      const sentries = getTableOfContentsSentries();
      let activeSentryId: T | undefined;

      sentries.forEach((sentry) => {
        const rect = sentry.getBoundingClientRect();
        if (rect.top <= (fromTop || 0)) {
          const sentryId = sentry.getAttribute('id');
          if (sentryId) {
            activeSentryId = sentryId as T;
          }
        }
      });

      if (activeSentryId) {
        setActiveTab(activeSentryId);
        onActiveTabChange?.(activeSentryId);

        if (tocRef?.current) {
          const tocItemId = `${id}-toc-${activeSentryId}`;

          if (
            document.activeElement &&
            document.activeElement.id !== tocItemId &&
            (document.activeElement === tocRef.current || tocRef.current.contains(document.activeElement))
          ) {
            (tocRef.current.querySelector(`#${tocItemId}`) as HTMLElement)?.focus();
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll, true);

    return () => {
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [getTableOfContentsSentries, setActiveTab, fromTop, isProgrammaticScrollRef, onActiveTabChange, tocRef, id]);
}
